import dayjs from 'dayjs'
import parser from 'cron-parser'
import cronstrue from 'cronstrue'
import { getFullNameOrEmail, User } from '../../../../redux/user'
import { ComplianceStatus, TeamMember } from '../../../../types'
import { FieldValidation, RequiredField, Task, TaskFormData, TaskLogPayload, TaskPayload, WeekDay } from '../types'
import { TaskFrequency, TaskLogStatus } from '../enums'
import { getTaskValidators } from './task-validation'

export const weekdays = new Map<WeekDay, number>([
  ['Sunday', 0],
  ['Monday', 1],
  ['Tuesday', 2],
  ['Wednesday', 3],
  ['Thursday', 4],
  ['Friday', 5],
  ['Saturday', 6],
])

export const days = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1
  let suffix = 'th'
  if (day % 10 === 1 && day !== 11) suffix = 'st'
  else if (day % 10 === 2 && day !== 12) suffix = 'nd'
  else if (day % 10 === 3 && day !== 13) suffix = 'rd'
  return `${day}${suffix}`
})

export const getUsersById = (users: TeamMember[]) => {
  return new Map(
    users.map(user => [Number(user.key_), user])
  )
}

export const getTasksById = (tasks: Task[] | null) => {
  return new Map(
    (tasks ?? []).map(task => [task.id, task])
  )
}

export const getUserFullName = (userId: number, usersById: Map<number, TeamMember>) => {
  const user = usersById.get(userId)
  const fullName = getFullNameOrEmail(user)
  return fullName === 'No Name' ? 'User Not Assigned' : fullName
}

const sortLogs = (task: Task, direction: 'asc' | 'desc') => {
  const logs = task.task_logs?.length ? structuredClone(task.task_logs) : []

  const sorted = logs.sort((a, b) => {
    const first = direction === 'asc' ? a : b
    const second = direction === 'asc' ? b : a
    const order = new Date(first.date).getTime() - new Date(second.date).getTime()
  
    return order
  })

  return sorted
}

export const getNextDueDate = (task: Task) => {
  if (task.cron) {
    const sortedLogs = sortLogs(task, 'desc')

    const baseline = sortedLogs.length
      ? sortedLogs[0].date
      : task.startDate

    const baselineDate = new Date(baseline)

    baselineDate.setHours(9, 0, 0, 0)

    const options = {
      currentDate: baselineDate,
      iterator: true
    }

    const interval = parser.parse(task.cron, options)
    const nextDue = interval.next()

    return nextDue.toString()
  } else if (task.weekly) {    
    const sortedLogs = sortLogs(task, 'desc')

    const baselineDate = sortedLogs.length
      ? sortedLogs[0].date
      : task.startDate

    const interval = sortedLogs.length
      ? Number(task.weekly)
      : 0

    const previousDueDate = sortedLogs.length
      ? dayjs(baselineDate).utc().endOf('day')
      : dayjs(baselineDate).startOf('day').utc().endOf('day')

    const nextDueDate = previousDueDate.millisecond(0).add(interval, 'weeks')

    return nextDueDate.toDate()
  } else {
    return new Date(task.startDate)
  }
}

export const nextDueDate = (task: Task) => dayjs(getNextDueDate(task)).utc().format('MM/DD/YYYY')

export const getTaskCompliance = (task: Task) => {
  const DAYS_UNTIL_OVERDUE = 2
  const now = dayjs()
  const twoDaysFromNow = dayjs().add(DAYS_UNTIL_OVERDUE, 'days')
  const dueDate = dayjs(nextDueDate(task))

  if (dueDate.isBefore(now, 'day')) {
    return ComplianceStatus.red
  } else if (dueDate.isBefore(twoDaysFromNow, 'day')) {
    return ComplianceStatus.yellow
  } else {
    return ComplianceStatus.green
  }
}

export const getTaskComplianceMessage = (status: ComplianceStatus) => {
  if (status === ComplianceStatus.red) {
    return 'Overdue'
  } else if (status === ComplianceStatus.yellow) {
    return 'Due soon'
  } else if (status === ComplianceStatus.green) {
    return 'Up to date'
  } else {
    return 'Unknown'
  }
}

export const getTaskHistoryComplianceMessage = (status: string) => {
  if (status === 'missed') {
    return 'Missed'
  } else if (status === 'skipped') {
    return 'Skipped'
  } else if (status === 'complete') {
    return 'Completed'
  } else {
    return 'Unknown'
  }
}

export const getFrequencyText = (task: Task) => {
  if (task.cron) {
    const cronString = cronstrue.toString(task.cron)
    return cronString.charAt(0).toLowerCase() + cronString.slice(1)
  } else {
    const weeks = Number(task.weekly) === 1 ? 'week' : 'weeks'
    return `every ${task.weekly} ${weeks}`
  }
}

export const getUserName = (userId: number, usersById: Map<number, TeamMember>) => {
  const user = usersById.get(userId)

  return user
    ? `${user.firstname || 'Team'}  ${user.lastname || 'Member'}`
    : 'User Not Assigned'
}

export const getCronSchedule = (formData: TaskFormData) => {
  let cron = null

  if (formData.frequency === TaskFrequency.Weekday) {
    const selectedDays = formData.weekdays
      .map(day => weekdays.get(day) ?? null)
      .filter(day => day !== null)

    cron = ['0', '9', '*', '*', selectedDays.toString()].join(' ')
  } else if (formData.frequency === TaskFrequency.Monthly) {
    cron = ['0', '9', days.indexOf(formData.dayOfMonth.toString()) + 1, '*', '*'].join(' ')
  }
  
  return cron
}

export const getWeeklySchedule = (formData: TaskFormData) => {
  const weekly = formData.frequency === TaskFrequency.Weekly
    ? String(formData.weekly)
    : null

  return weekly
}

const getEndDate = (formData: TaskFormData) => {
  if (formData.noEndDate || formData.frequency === TaskFrequency.OneTime) {
    return null
  } else {
    return formData.endDate && formData.endDate.toISOString()
  }
}

export const getTaskPayload = (customerNumber: string, user: User, task: Task | null, formData: TaskFormData) => {
  const payload: TaskPayload = {
    customer_number: customerNumber,
    name: formData.name,
    cron: getCronSchedule(formData),
    assignedBy: Number(user.key_),
    assignedTo: Number(formData.assignedTo),
    description: formData.description || null,
    startDate: formData.startDate!.toISOString(),
    endDate: getEndDate(formData),
    reminder: formData.reminder,
    active: true,
    weekly: getWeeklySchedule(formData),
    type: null,
  }

  if (task) {
    payload.id = task.id
  }

  return payload
}

export const getTaskLogPayload = (task: Task, currentUser: User, status: TaskLogStatus) => {
  const nextDueDate = getNextDueDate(task)

  const payload: TaskLogPayload = {
    status,
    userId: currentUser.key_!,
    taskId: task.id,
    date: dayjs(nextDueDate).toISOString(),
    reminder_attempts: null,
    note: null,
    addressed_on: null,
  }

  return payload
}

export const cronToModel = (task: Task | null): { frequency: TaskFrequency, repeats?: string | string[] | number } => {
  if (task && task.cron && task.cron.length > 0) {
    const weekdayCron = task.cron.split(' ')[4].split(',')
    const dailyCron = task.cron.split(' ')[2]

    if (weekdayCron.length && weekdayCron[0] !== '*') {
      const selectedWeekdays = Array.from(weekdays.keys())
        .filter((day) => {
          return weekdayCron.find(weekday => weekdays.get(day) === parseInt(weekday, 10))
        })
      return { frequency: TaskFrequency.Weekday, repeats: selectedWeekdays }
    } else if (dailyCron.length && dailyCron !== '*') {
      const selectedDayOfMonth = days[parseInt(dailyCron, 10) - 1]
      return { frequency: TaskFrequency.Monthly, repeats: selectedDayOfMonth }
    }
  } else if (task && task.weekly) {
    return { frequency: TaskFrequency.Weekly, repeats: parseInt(task.weekly, 10) }
  }
  return { frequency: TaskFrequency.OneTime }
}

export const getIsOneTimeTask = (task: Task) => {
  const isOneTime = task.cron === null && task.weekly === null
  return isOneTime
}

export const getRequiredFields = (formData: TaskFormData) => {
  const base: RequiredField[] = ['name', 'assignedTo', 'startDate' ]

  const fieldsByFrequency = new Map<TaskFrequency, RequiredField[]>([
    [TaskFrequency.Weekday, [ 'weekdays', 'endDate' ]],
    [TaskFrequency.Weekly, [ 'weekly', 'endDate' ]],
    [TaskFrequency.Monthly, [ 'dayOfMonth', 'endDate' ]],
  ])

  const frequency = fieldsByFrequency.get(formData.frequency) ?? []
  const required = [base, frequency].flat()

  return required
}

export const getFormValidation = (task: Task | null, formData: TaskFormData) => {
  const taskValidators = getTaskValidators(task)

  const formValidation: FieldValidation = {
    name: taskValidators.name(formData.name),
    assignedTo: taskValidators.assignedTo(formData.assignedTo),
    startDate: taskValidators.startDate(formData.startDate),
    endDate: taskValidators.endDate(formData.startDate, formData.endDate, formData.noEndDate),
    weekdays: taskValidators.weekdays(formData.weekdays),
    weekly: taskValidators.weekly(formData.weekly),
    dayOfMonth: taskValidators.dayOfMonth(formData.dayOfMonth),
  }

  return formValidation
}

export const getIsValidForm = (requiredFields: RequiredField[], formValidation: FieldValidation) => {
  const isValidForm = requiredFields.every(field => {
    const validation = formValidation[field]
    return validation.isValid
  })

  return isValidForm
}
